import React, { useState } from "react";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  InputGroup,
  InputRightElement,
  IconButton,
  Stack,
  VStack,
  HStack,
  Heading,
  Text,
  Link,
  useToast,
  Icon,
  Flex,
  Image,
  Grid,
  GridItem,
  Divider,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Avatar,
  Tooltip,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
} from "@chakra-ui/react";
import { ViewIcon, ViewOffIcon, LockIcon } from "@chakra-ui/icons";
import { FaUserLock, FaGoogle } from "react-icons/fa";
import { useAuth } from "../context/AuthContext";
import { buildTenantRedirectUrl } from "../utils/tenant";
import { useNavigate } from "react-router-dom";
import { FORGOT_PASSWORD_URL } from "../services/api";

const LoginPage = ({ onSwitch }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [registerForm, setRegisterForm] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
    phone: "",
    companyName: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [showRegisterPassword, setShowRegisterPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [lastUserEmail, setLastUserEmail] = useState(() => {
    // Load last logged-in email from localStorage on mount
    return localStorage.getItem("lastUserEmail") || null;
  });
  const [showQuickSignIn, setShowQuickSignIn] = useState(!!lastUserEmail);
  const [quickSignInActive, setQuickSignInActive] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState("");
  const [isSendingReset, setIsSendingReset] = useState(false);
  const {
    isOpen: isForgotPasswordOpen,
    onOpen: onForgotPasswordOpen,
    onClose: onForgotPasswordClose,
  } = useDisclosure();
  const toast = useToast();
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const handleLoginChange = (e) =>
    setLoginForm({ ...loginForm, [e.target.name]: e.target.value });

  const handleRegisterChange = (e) =>
    setRegisterForm({ ...registerForm, [e.target.name]: e.target.value });

  const handleLoginKeyPress = (e) => {
    if (e.key === "Enter") {
      handleLogin();
    } else if (e.key === "Escape" && quickSignInActive) {
      handleUseDifferentAccount();
    }
  };

  const handleRegisterKeyPress = (e) => {
    if (e.key === "Enter") {
      handleRegister();
    }
  };

  const handleQuickSignIn = () => {
    // Pre-fill email from last user and focus on password field
    setLoginForm({ email: lastUserEmail, password: "" });
    setQuickSignInActive(true);
    setActiveTab(0); // Ensure we're on login tab
    // Focus on password field after render
    setTimeout(() => {
      const passwordInput = document.querySelector('input[name="password"]');
      if (passwordInput) passwordInput.focus();
    }, 100);
  };

  const handleUseDifferentAccount = () => {
    setLoginForm({ email: "", password: "" });
    setQuickSignInActive(false);
    setShowQuickSignIn(false);
  };

  const handleClearQuickSignIn = () => {
    localStorage.removeItem("lastUserEmail");
    setLastUserEmail(null);
    setShowQuickSignIn(false);
    setQuickSignInActive(false);
    setLoginForm({ email: "", password: "" });
  };

  // Utility functions for email display
  const maskEmail = (email) => {
    if (!email) return "";
    const [username, domain] = email.split("@");
    if (username.length <= 3) return email;
    const visible = username.slice(0, 3);
    const masked = "*".repeat(Math.min(username.length - 3, 5));
    return `${visible}${masked}@${domain}`;
  };

  const getDisplayName = (email) => {
    if (!email) return "";
    const username = email.split("@")[0];
    return username.charAt(0).toUpperCase() + username.slice(1);
  };

  const handleForgotPassword = async () => {
    if (!forgotPasswordEmail) {
      return toast({
        title: "Email Required",
        description: "Please enter your email address.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
    }

    setIsSendingReset(true);
    try {
      const response = await fetch(FORGOT_PASSWORD_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: forgotPasswordEmail }),
      });

      const data = await response.json();

      if (response.ok) {
        toast({
          title: "Reset Link Sent",
          description: "Check your email for password reset instructions.",
          status: "success",
          duration: 5000,
          isClosable: true,
        });
        setForgotPasswordEmail("");
        onForgotPasswordClose();
      } else {
        throw new Error(data.message || "Failed to send reset link");
      }
    } catch (error) {
      toast({
        title: "Error",
        description:
          error.message || "Failed to send reset link. Please try again.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setIsSendingReset(false);
    }
  };

  const handleLogin = async () => {
    if (!loginForm.email || !loginForm.password) {
      return toast({
        title: "Missing Fields",
        description: "Email and Password are required.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
    }

    setIsLoading(true);
    const result = await login(loginForm.email, loginForm.password);
    setIsLoading(false);

    if (result.success) {
      // Save the email for quick sign in next time
      localStorage.setItem("lastUserEmail", loginForm.email);
      setLastUserEmail(loginForm.email);

      toast({
        title: "Login Successful",
        description: "Welcome back!",
        status: "success",
        duration: 2000,
        isClosable: true,
      });

      const tenantSlug = result.company?.slug;
      const tenantRedirectUrl = buildTenantRedirectUrl(tenantSlug);
      if (tenantRedirectUrl) {
        window.location.assign(tenantRedirectUrl);
        return;
      }

      navigate("/");
    } else {
      toast({
        title: "Login Failed",
        description: result.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleRegister = async () => {
    if (
      !registerForm.name ||
      !registerForm.email ||
      !registerForm.password ||
      !registerForm.companyName
    ) {
      return toast({
        title: "Missing Fields",
        description: "Name, Email, Password, and Company Name are required.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
    }

    if (registerForm.password !== registerForm.confirmPassword) {
      return toast({
        title: "Password Mismatch",
        description: "Passwords do not match.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }

    if (registerForm.password.length < 6) {
      return toast({
        title: "Weak Password",
        description: "Password must be at least 6 characters long.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
    }

    setIsLoading(true);
    const result = await register({
      name: registerForm.name,
      email: registerForm.email,
      password: registerForm.password,
      phone: registerForm.phone,
      companyName: registerForm.companyName,
    });
    setIsLoading(false);

    if (result.success) {
      toast({
        title: "Registration Successful",
        description: "Welcome to NoraHuman!",
        status: "success",
        duration: 2000,
        isClosable: true,
      });

      const tenantSlug = result.company?.slug;
      const tenantRedirectUrl = buildTenantRedirectUrl(tenantSlug);
      if (tenantRedirectUrl) {
        window.location.assign(tenantRedirectUrl);
        return;
      }

      navigate("/");
    } else {
      toast({
        title: "Registration Failed",
        description: result.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  return (
    <Flex
      width="100vw"
      height="100vh"
      bg="gray.50"
      align="center"
      justify="center"
      // p={{ base: 4, md: 8 }}
    >
      <Flex
        maxW="100%"
        w="full"
        h={{ base: "auto", md: "100%" }}
        bg="white"
        borderRadius="3xl"
        overflow="hidden"
        boxShadow="2xl"
        flexDir={{ base: "column", md: "row" }}
      >
        {/* Left Panel - Image Grid */}
        <Box
          display={{ base: "none", md: "block" }}
          width="50%"
          position="relative"
          bgGradient="linear(to-br, gray.50, blue.50, purple.50)"
        >
          <Grid
            templateColumns="repeat(3, 1fr)"
            templateRows="repeat(4, 1fr)"
            gap={2}
            h="full"
            p={4}
          >
            {/* Grid images with stat overlays */}
            <GridItem
              colSpan={1}
              rowSpan={1}
              borderRadius="xl"
              overflow="hidden"
              bg="#1e3a8a"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <VStack spacing={2}>
                <Icon as={FaUserLock} boxSize={8} color="white" />
                <Text fontSize="xs" color="white" fontWeight="semibold">
                  HR
                </Text>
              </VStack>
            </GridItem>
            <GridItem
              colSpan={2}
              rowSpan={2}
              borderRadius="xl"
              overflow="hidden"
              bg="#FF6B5A"
              position="relative"
            >
              <VStack
                justify="center"
                align="center"
                h="full"
                p={6}
                color="white"
              >
                <Heading fontSize="5xl" fontWeight="bold">
                  41%
                </Heading>
                <Text fontSize="sm" textAlign="center" fontWeight="medium">
                  of recruiters say entry-level positions are the hardest to
                  fill.
                </Text>
              </VStack>
            </GridItem>
            <GridItem
              colSpan={1}
              rowSpan={1}
              borderRadius="xl"
              overflow="hidden"
              bg="#0f766e"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <VStack spacing={2}>
                <Box
                  boxSize={8}
                  borderRadius="full"
                  bg="white"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  <Box boxSize={4} borderRadius="full" bg="#0f766e" />
                </Box>
                <Text fontSize="xs" color="white" fontWeight="semibold">
                  Recruitment
                </Text>
              </VStack>
            </GridItem>
            <GridItem
              colSpan={2}
              rowSpan={1}
              borderRadius="xl"
              overflow="hidden"
              bg="#1e40af"
            >
              <HStack
                h="full"
                spacing={0}
                align="center"
                justify="space-around"
                px={6}
              >
                <VStack spacing={1}>
                  <Box
                    boxSize={10}
                    borderRadius="md"
                    bg="white"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Text fontSize="lg" fontWeight="bold" color="#1e40af">
                      A
                    </Text>
                  </Box>
                  <Text fontSize="2xs" color="white">
                    Analytics
                  </Text>
                </VStack>
                <VStack spacing={1}>
                  <Box
                    boxSize={10}
                    borderRadius="md"
                    bg="white"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Text fontSize="lg" fontWeight="bold" color="#1e40af">
                      T
                    </Text>
                  </Box>
                  <Text fontSize="2xs" color="white">
                    Tracking
                  </Text>
                </VStack>
                <VStack spacing={1}>
                  <Box
                    boxSize={10}
                    borderRadius="md"
                    bg="white"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Text fontSize="lg" fontWeight="bold" color="#1e40af">
                      M
                    </Text>
                  </Box>
                  <Text fontSize="2xs" color="white">
                    Management
                  </Text>
                </VStack>
              </HStack>
            </GridItem>
            <GridItem
              colSpan={1}
              rowSpan={1}
              borderRadius="xl"
              overflow="hidden"
              bg="#065f46"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <VStack spacing={2}>
                <Box
                  boxSize={8}
                  borderRadius="md"
                  bg="white"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  <Text fontSize="sm" fontWeight="bold" color="#065f46">
                    PR
                  </Text>
                </Box>
                <Text fontSize="xs" color="white" fontWeight="semibold">
                  Procurement
                </Text>
              </VStack>
            </GridItem>
            <GridItem
              colSpan={2}
              rowSpan={2}
              borderRadius="xl"
              overflow="hidden"
              bg="#4CAF50"
              position="relative"
            >
              <VStack
                justify="center"
                align="center"
                h="full"
                p={6}
                color="white"
              >
                <Heading fontSize="5xl" fontWeight="bold">
                  76%
                </Heading>
                <Text fontSize="sm" textAlign="center" fontWeight="medium">
                  of hiring managers admit attracting the right job candidates
                  is their greatest challenge.
                </Text>
              </VStack>
            </GridItem>
            <GridItem
              colSpan={1}
              rowSpan={2}
              borderRadius="xl"
              overflow="hidden"
              bg="#7c3aed"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <VStack spacing={3}>
                <Box
                  boxSize={16}
                  borderRadius="full"
                  border="3px solid white"
                  bg="whiteAlpha.300"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  <Box
                    boxSize={12}
                    borderRadius="full"
                    bg="white"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    p={1.5}
                  >
                    <Image
                      src="/image.png"
                      alt="NoraHuman Logo"
                      boxSize="100%"
                      objectFit="contain"
                    />
                  </Box>
                </Box>
                <Text
                  fontSize="sm"
                  color="white"
                  fontWeight="semibold"
                  textAlign="center"
                >
                  NoraHuman
                </Text>
              </VStack>
            </GridItem>
          </Grid>
        </Box>

        {/* Right Panel - Login/Register Form */}
        <Flex width={{ base: "100%", md: "50%" }} bg="white" direction="column">
          {/* Header */}
          <HStack justify="flex-end" p={6} spacing={4}>
            <Text fontSize="sm" color="gray.600">
              Need help?
            </Text>
            <Link
              fontSize="sm"
              color="#0B4885"
              fontWeight="semibold"
              _hover={{ textDecoration: "underline" }}
            >
              Contact Support
            </Link>
          </HStack>

          {/* Form Container */}
          <Flex
            flex="1"
            direction="column"
            px={{ base: 6, md: 10 }}
            py={8}
            overflowY="auto"
            justify="flex-start"
            align="center"
          >
            <Box width="100%" maxW="420px">
              <Tabs
                index={activeTab}
                onChange={setActiveTab}
                colorScheme="blue"
                variant="soft-rounded"
                isFitted
              >
                <TabList
                  mb={8}
                  bg="gray.100"
                  p={1.5}
                  borderRadius="xl"
                  boxShadow="sm"
                >
                  <Tab
                    flex="1"
                    fontWeight="600"
                    fontSize="md"
                    py={3}
                    _selected={{ bg: "#0B4885", color: "white", shadow: "md" }}
                    _hover={{ bg: "gray.200" }}
                    transition="all 0.2s"
                    borderRadius="lg"
                  >
                    Sign In
                  </Tab>
                  <Tab
                    flex="1"
                    fontWeight="600"
                    fontSize="md"
                    py={3}
                    _selected={{ bg: "#0B4885", color: "white", shadow: "md" }}
                    _hover={{ bg: "gray.200" }}
                    transition="all 0.2s"
                    borderRadius="lg"
                  >
                    Sign Up
                  </Tab>
                </TabList>

                <TabPanels>
                  {/* Login Tab */}
                  <TabPanel p={0}>
                    <VStack spacing={7} align="stretch">
                      {/* Quick Sign-In Card (Account Recall) */}
                      {showQuickSignIn &&
                        lastUserEmail &&
                        !quickSignInActive && (
                          <Box>
                            <Tooltip
                              label="This device remembers your account"
                              hasArrow
                              bg="gray.700"
                              color="white"
                              placement="top"
                              fontSize="xs"
                            >
                              <Box
                                p={4}
                                borderRadius="xl"
                                border="2px solid"
                                borderColor="gray.200"
                                bg="white"
                                cursor="pointer"
                                transition="all 0.2s"
                                _hover={{
                                  borderColor: "#0B4885",
                                  bg: "blue.50",
                                  transform: "translateY(-2px)",
                                  boxShadow: "md",
                                }}
                                _active={{
                                  transform: "translateY(0)",
                                }}
                                onClick={handleQuickSignIn}
                              >
                                <HStack spacing={4}>
                                  <Avatar
                                    name={lastUserEmail}
                                    size="md"
                                    bg="#0B4885"
                                    color="white"
                                    fontWeight="bold"
                                  />
                                  <VStack
                                    align="flex-start"
                                    spacing={0}
                                    flex="1"
                                  >
                                    <HStack spacing={2}>
                                      <Text
                                        fontSize="md"
                                        fontWeight="600"
                                        color="gray.800"
                                      >
                                        Continue as{" "}
                                        {getDisplayName(lastUserEmail)}
                                      </Text>
                                      <Icon
                                        as={LockIcon}
                                        boxSize={3}
                                        color="gray.500"
                                      />
                                    </HStack>
                                    <Text fontSize="sm" color="gray.600">
                                      {maskEmail(lastUserEmail)}
                                    </Text>
                                    <Text fontSize="xs" color="gray.500" mt={1}>
                                      Use password to continue
                                    </Text>
                                  </VStack>
                                  <IconButton
                                    icon={<ViewOffIcon />}
                                    size="sm"
                                    variant="ghost"
                                    aria-label="Remove saved account"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleClearQuickSignIn();
                                    }}
                                    _hover={{
                                      bg: "red.50",
                                      color: "red.600",
                                    }}
                                  />
                                </HStack>
                              </Box>
                            </Tooltip>
                            <Link
                              fontSize="sm"
                              color="gray.600"
                              fontWeight="500"
                              mt={3}
                              display="block"
                              textAlign="center"
                              onClick={handleUseDifferentAccount}
                              _hover={{
                                color: "#0B4885",
                                textDecoration: "underline",
                              }}
                              cursor="pointer"
                            >
                              Use another account
                            </Link>
                          </Box>
                        )}

                      {/* Login Title */}
                      <VStack spacing={2} align="flex-start">
                        <Heading
                          fontSize="3xl"
                          color="gray.800"
                          fontWeight="700"
                          letterSpacing="-0.5px"
                        >
                          Welcome Back
                        </Heading>
                        <Text fontSize="md" color="gray.600" fontWeight="400">
                          {quickSignInActive
                            ? `Signing in as ${getDisplayName(lastUserEmail)}`
                            : "Sign in to continue to NoraHuman"}
                        </Text>
                      </VStack>

                      {/* Login Form Fields */}
                      <VStack spacing={5}>
                        <FormControl>
                          <FormLabel
                            fontSize="sm"
                            fontWeight="600"
                            color="gray.700"
                            mb={2}
                          >
                            Email Address
                          </FormLabel>
                          <Input
                            name="email"
                            type="email"
                            value={loginForm.email}
                            onChange={handleLoginChange}
                            onKeyPress={handleLoginKeyPress}
                            placeholder="Enter your email"
                            size="lg"
                            bg={quickSignInActive ? "gray.100" : "gray.50"}
                            border="1px solid"
                            borderColor="gray.200"
                            _hover={{
                              borderColor: "gray.300",
                              bg: quickSignInActive ? "gray.100" : "white",
                            }}
                            _focus={{
                              bg: quickSignInActive ? "gray.100" : "white",
                              borderColor: "#0B4885",
                              boxShadow: "0 0 0 1px #0B4885",
                            }}
                            borderRadius="lg"
                            isReadOnly={quickSignInActive}
                            cursor={quickSignInActive ? "not-allowed" : "text"}
                          />
                        </FormControl>

                        <FormControl>
                          <FormLabel
                            fontSize="sm"
                            fontWeight="600"
                            color="gray.700"
                            mb={2}
                          >
                            Password
                          </FormLabel>
                          <InputGroup>
                            <Input
                              type={showLoginPassword ? "text" : "password"}
                              name="password"
                              value={loginForm.password}
                              onChange={handleLoginChange}
                              onKeyPress={handleLoginKeyPress}
                              placeholder="Enter your password"
                              size="lg"
                              bg="gray.50"
                              border="1px solid"
                              borderColor="gray.200"
                              _hover={{ borderColor: "gray.300", bg: "white" }}
                              _focus={{
                                bg: "white",
                                borderColor: "#0B4885",
                                boxShadow: "0 0 0 1px #0B4885",
                              }}
                              borderRadius="lg"
                            />
                            <InputRightElement width="3rem">
                              <IconButton
                                h="1.75rem"
                                size="sm"
                                onClick={() =>
                                  setShowLoginPassword(!showLoginPassword)
                                }
                                icon={
                                  showLoginPassword ? (
                                    <ViewOffIcon />
                                  ) : (
                                    <ViewIcon />
                                  )
                                }
                                aria-label={
                                  showLoginPassword
                                    ? "Hide password"
                                    : "Show password"
                                }
                                variant="ghost"
                              />
                            </InputRightElement>
                          </InputGroup>
                        </FormControl>

                        <HStack justify="flex-end" w="full">
                          <Link
                            fontSize="sm"
                            color="#0B4885"
                            fontWeight="600"
                            _hover={{ textDecoration: "underline" }}
                            cursor="pointer"
                            onClick={() => {
                              setForgotPasswordEmail(loginForm.email);
                              onForgotPasswordOpen();
                            }}
                          >
                            Forgot password?
                          </Link>
                        </HStack>

                        <Button
                          bg="#0B4885"
                          color="white"
                          size="lg"
                          width="100%"
                          onClick={handleLogin}
                          isLoading={isLoading}
                          fontSize="md"
                          fontWeight="600"
                          _hover={{
                            bg: "#0a3d6e",
                            transform: "translateY(-1px)",
                            boxShadow: "lg",
                          }}
                          _active={{
                            bg: "#083156",
                            transform: "translateY(0)",
                          }}
                          mt={2}
                          borderRadius="lg"
                          transition="all 0.2s"
                          boxShadow="md"
                        >
                          {quickSignInActive ? "Continue" : "Sign In"}
                        </Button>
                      </VStack>

                      {/* Divider */}
                      <HStack spacing={4} my={1}>
                        <Divider borderColor="gray.300" />
                        <Text
                          fontSize="sm"
                          color="gray.500"
                          fontWeight="500"
                          whiteSpace="nowrap"
                        >
                          OR
                        </Text>
                        <Divider borderColor="gray.300" />
                      </HStack>

                      {/* Google Sign In */}
                      <Button
                        variant="outline"
                        size="lg"
                        width="100%"
                        leftIcon={<Icon as={FaGoogle} color="red.500" />}
                        fontSize="md"
                        fontWeight="600"
                        borderColor="gray.300"
                        color="gray.700"
                        _hover={{
                          bg: "gray.50",
                          borderColor: "gray.400",
                          transform: "translateY(-1px)",
                          boxShadow: "md",
                        }}
                        _active={{ bg: "gray.100", transform: "translateY(0)" }}
                        borderRadius="lg"
                        transition="all 0.2s"
                      >
                        Sign in with Google
                      </Button>
                    </VStack>
                  </TabPanel>

                  {/* Register Tab */}
                  <TabPanel p={0}>
                    <VStack spacing={6} align="stretch">
                      {/* Register Title */}
                      <VStack spacing={2} align="flex-start" mb={2}>
                        <Heading
                          fontSize="3xl"
                          fontWeight="700"
                          letterSpacing="-0.5px"
                          color="gray.800"
                        >
                          Create Your Account
                        </Heading>
                        <Text fontSize="md" color="gray.600">
                          Start your HR management journey today
                        </Text>
                      </VStack>

                      {/* Register Form Fields */}
                      <VStack spacing={5}>
                        <FormControl isRequired>
                          <FormLabel
                            fontSize="sm"
                            fontWeight="600"
                            color="gray.700"
                            mb={2}
                          >
                            Full Name
                          </FormLabel>
                          <Input
                            name="name"
                            value={registerForm.name}
                            onChange={handleRegisterChange}
                            onKeyPress={handleRegisterKeyPress}
                            placeholder="Enter your full name"
                            size="lg"
                            bg="gray.50"
                            border="1px solid"
                            borderColor="gray.200"
                            _hover={{ borderColor: "gray.300", bg: "white" }}
                            _focus={{
                              bg: "white",
                              borderColor: "#0B4885",
                              boxShadow: "0 0 0 1px #0B4885",
                            }}
                            borderRadius="lg"
                          />
                        </FormControl>

                        <FormControl isRequired>
                          <FormLabel
                            fontSize="sm"
                            fontWeight="600"
                            color="gray.700"
                            mb={2}
                          >
                            Email Address
                          </FormLabel>
                          <Input
                            name="email"
                            type="email"
                            value={registerForm.email}
                            onChange={handleRegisterChange}
                            onKeyPress={handleRegisterKeyPress}
                            placeholder="Enter your email"
                            size="lg"
                            bg="gray.50"
                            border="1px solid"
                            borderColor="gray.200"
                            _hover={{ borderColor: "gray.300", bg: "white" }}
                            _focus={{
                              bg: "white",
                              borderColor: "#0B4885",
                              boxShadow: "0 0 0 1px #0B4885",
                            }}
                            borderRadius="lg"
                          />
                        </FormControl>

                        <FormControl>
                          <FormLabel
                            fontSize="sm"
                            fontWeight="600"
                            color="gray.700"
                            mb={2}
                          >
                            Phone Number
                          </FormLabel>
                          <Input
                            name="phone"
                            type="tel"
                            value={registerForm.phone}
                            onChange={handleRegisterChange}
                            onKeyPress={handleRegisterKeyPress}
                            placeholder="Enter your phone number"
                            size="lg"
                            bg="gray.50"
                            border="1px solid"
                            borderColor="gray.200"
                            _hover={{ borderColor: "gray.300", bg: "white" }}
                            _focus={{
                              bg: "white",
                              borderColor: "#0B4885",
                              boxShadow: "0 0 0 1px #0B4885",
                            }}
                            borderRadius="lg"
                          />
                        </FormControl>

                        <FormControl isRequired>
                          <FormLabel
                            fontSize="sm"
                            fontWeight="600"
                            color="gray.700"
                            mb={2}
                          >
                            Company Name
                          </FormLabel>
                          <Input
                            name="companyName"
                            value={registerForm.companyName}
                            onChange={handleRegisterChange}
                            onKeyPress={handleRegisterKeyPress}
                            placeholder="Enter your company name"
                            size="lg"
                            bg="gray.50"
                            border="1px solid"
                            borderColor="gray.200"
                            _hover={{ borderColor: "gray.300", bg: "white" }}
                            _focus={{
                              bg: "white",
                              borderColor: "#0B4885",
                              boxShadow: "0 0 0 1px #0B4885",
                            }}
                            borderRadius="lg"
                          />
                        </FormControl>

                        <FormControl isRequired>
                          <FormLabel
                            fontSize="sm"
                            fontWeight="600"
                            color="gray.700"
                            mb={2}
                          >
                            Password
                          </FormLabel>
                          <InputGroup>
                            <Input
                              type={showRegisterPassword ? "text" : "password"}
                              name="password"
                              value={registerForm.password}
                              onChange={handleRegisterChange}
                              onKeyPress={handleRegisterKeyPress}
                              placeholder="Create a password"
                              size="lg"
                              bg="gray.50"
                              border="1px solid"
                              borderColor="gray.200"
                              _hover={{ borderColor: "gray.300", bg: "white" }}
                              _focus={{
                                bg: "white",
                                borderColor: "#0B4885",
                                boxShadow: "0 0 0 1px #0B4885",
                              }}
                              borderRadius="lg"
                            />
                            <InputRightElement width="3rem">
                              <IconButton
                                h="1.75rem"
                                size="sm"
                                onClick={() =>
                                  setShowRegisterPassword(!showRegisterPassword)
                                }
                                icon={
                                  showRegisterPassword ? (
                                    <ViewOffIcon />
                                  ) : (
                                    <ViewIcon />
                                  )
                                }
                                aria-label={
                                  showRegisterPassword
                                    ? "Hide password"
                                    : "Show password"
                                }
                                variant="ghost"
                              />
                            </InputRightElement>
                          </InputGroup>
                        </FormControl>

                        <FormControl isRequired>
                          <FormLabel
                            fontSize="sm"
                            fontWeight="600"
                            color="gray.700"
                            mb={2}
                          >
                            Confirm Password
                          </FormLabel>
                          <InputGroup>
                            <Input
                              type={showConfirmPassword ? "text" : "password"}
                              name="confirmPassword"
                              value={registerForm.confirmPassword}
                              onChange={handleRegisterChange}
                              onKeyPress={handleRegisterKeyPress}
                              placeholder="Confirm your password"
                              size="lg"
                              bg="gray.50"
                              border="1px solid"
                              borderColor="gray.200"
                              _hover={{ borderColor: "gray.300", bg: "white" }}
                              _focus={{
                                bg: "white",
                                borderColor: "#0B4885",
                                boxShadow: "0 0 0 1px #0B4885",
                              }}
                              borderRadius="lg"
                            />
                            <InputRightElement width="3rem">
                              <IconButton
                                h="1.75rem"
                                size="sm"
                                onClick={() =>
                                  setShowConfirmPassword(!showConfirmPassword)
                                }
                                icon={
                                  showConfirmPassword ? (
                                    <ViewOffIcon />
                                  ) : (
                                    <ViewIcon />
                                  )
                                }
                                aria-label={
                                  showConfirmPassword
                                    ? "Hide password"
                                    : "Show password"
                                }
                                variant="ghost"
                              />
                            </InputRightElement>
                          </InputGroup>
                        </FormControl>

                        <Button
                          bg="#0B4885"
                          color="white"
                          size="lg"
                          width="100%"
                          onClick={handleRegister}
                          isLoading={isLoading}
                          fontSize="md"
                          fontWeight="600"
                          _hover={{
                            bg: "#0a3d6e",
                            transform: "translateY(-1px)",
                            boxShadow: "lg",
                          }}
                          _active={{
                            bg: "#083156",
                            transform: "translateY(0)",
                          }}
                          mt={2}
                          borderRadius="lg"
                          transition="all 0.2s"
                          boxShadow="md"
                        >
                          Create Account
                        </Button>

                        <Text
                          fontSize="xs"
                          color="gray.500"
                          textAlign="center"
                          lineHeight="1.5"
                        >
                          By signing up, you agree to our{" "}
                          <Link
                            color="#0B4885"
                            fontWeight="600"
                            _hover={{ textDecoration: "underline" }}
                          >
                            Terms of Service
                          </Link>{" "}
                          and{" "}
                          <Link
                            color="#0B4885"
                            fontWeight="600"
                            _hover={{ textDecoration: "underline" }}
                          >
                            Privacy Policy
                          </Link>
                        </Text>
                      </VStack>
                    </VStack>
                  </TabPanel>
                </TabPanels>
              </Tabs>
            </Box>
          </Flex>
        </Flex>
      </Flex>

      {/* Forgot Password Modal */}
      <Modal
        isOpen={isForgotPasswordOpen}
        onClose={onForgotPasswordClose}
        isCentered
      >
        <ModalOverlay backdropFilter="blur(4px)" />
        <ModalContent borderRadius="xl" mx={4}>
          <ModalHeader>
            <VStack align="flex-start" spacing={1}>
              <Heading fontSize="2xl" color="gray.800">
                Reset Password
              </Heading>
              <Text fontSize="sm" fontWeight="normal" color="gray.600">
                Enter your email address and we'll send you a link to reset your
                password
              </Text>
            </VStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <FormControl>
              <FormLabel fontSize="sm" fontWeight="600" color="gray.700">
                Email Address
              </FormLabel>
              <Input
                type="email"
                value={forgotPasswordEmail}
                onChange={(e) => setForgotPasswordEmail(e.target.value)}
                placeholder="Enter your email"
                size="lg"
                bg="gray.50"
                color="gray.800"
                border="1px solid"
                borderColor="gray.200"
                _placeholder={{ color: "gray.500" }}
                _hover={{ borderColor: "gray.300", bg: "white" }}
                _focus={{
                  bg: "white",
                  borderColor: "#0B4885",
                  boxShadow: "0 0 0 1px #0B4885",
                }}
                borderRadius="lg"
                autoFocus
                onKeyPress={(e) => {
                  if (e.key === "Enter") {
                    handleForgotPassword();
                  }
                }}
              />
            </FormControl>
          </ModalBody>
          <ModalFooter gap={3}>
            <Button
              variant="ghost"
              onClick={onForgotPasswordClose}
              size="lg"
              fontWeight="600"
              _hover={{ bg: "gray.100" }}
            >
              Cancel
            </Button>
            <Button
              bg="#0B4885"
              color="white"
              size="lg"
              onClick={handleForgotPassword}
              isLoading={isSendingReset}
              loadingText="Sending..."
              fontWeight="600"
              _hover={{
                bg: "#0a3d6e",
                transform: "translateY(-1px)",
                boxShadow: "lg",
              }}
              _active={{
                bg: "#083156",
                transform: "translateY(0)",
              }}
              borderRadius="lg"
              transition="all 0.2s"
            >
              Send Reset Link
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Flex>
  );
};

export default LoginPage;
